import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, Loader2, CheckCircle2, XCircle, Repeat } from 'lucide-react';

// ── The ten lines of callback code, dripped one line at a time ───────────
const CALLBACK_LINES = [
  'from kojiru_recourse_blueprint.kypa_client import KYPAClient',
  '',
  'kypa = KYPAClient()',
  '',
  'async def on_authorization(auth_request):',
  '    credential = auth_request.metadata.get("kypa_credential")',
  '    verdict = await kypa.verify(credential, intent={',
  '        "amount_cents": auth_request.amount_cents,   # 60000',
  '        "category":     auth_request.mcc_category,   # travel_hotel',
  '    })',
  '    if verdict.refused:',
  '        return "decline", verdict.reason',
];

// ── Sequence timeline: each step\u2019s duration in ms (~20-second loop) ────
const STEP_DURATIONS = [
  1400, // 0 idle title beat
  2000, // 1 agent authorization request appears
  4800, // 2 callback types code
  2400, // 3 verify HTTP call fires (spinner)
  2600, // 4 KYPA verify response returns (refused)
  4200, // 5 verdict panel confirms decline
  1600, // 6 hold, replay chip appears
];

export default function CallbackDemoHero() {
  const [step, setStep] = useState(0);
  const [loopCount, setLoopCount] = useState(0);
  const [typedLines, setTypedLines] = useState(0);

  // Advance through step 0..6 loop
  useEffect(() => {
    const t = setTimeout(() => {
      const next = (step + 1) % STEP_DURATIONS.length;
      setStep(next);
      if (next === 0) setLoopCount((c) => c + 1);
    }, STEP_DURATIONS[step]);
    return () => clearTimeout(t);
  }, [step]);

  // Drip callback code lines during step 2; snap to full on step >= 3; blank otherwise
  useEffect(() => {
    if (step >= 3) {
      setTypedLines(CALLBACK_LINES.length);
      return;
    }
    if (step !== 2) {
      setTypedLines(0);
      return;
    }
    const perLine = STEP_DURATIONS[2] / CALLBACK_LINES.length;
    const started = Date.now();
    let canceled = false;
    const tick = () => {
      if (canceled) return;
      const elapsed = Date.now() - started;
      setTypedLines(Math.min(CALLBACK_LINES.length, Math.floor(elapsed / perLine) + 1));
      if (elapsed < STEP_DURATIONS[2]) {
        setTimeout(tick, Math.max(60, perLine / 3));
      }
    };
    tick();
    return () => {
      canceled = true;
    };
  }, [step]);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-slate-950" data-testid="callback-demo-hero">
      {/* Terminal chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 border-b border-white/5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/60"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60"></span>
        <span className="ml-3 text-[10px] font-mono text-slate-500 tracking-widest uppercase">
          Live demo &middot; $200 hotel &rarr; agent presents $600 &rarr; decline
        </span>
        <span className="ml-auto text-[10px] font-mono text-slate-600" data-testid="loop-counter">
          loop #{loopCount + 1}
        </span>
      </div>

      {/* Three-panel demo */}
      <div className="grid md:grid-cols-3 gap-0 min-h-[420px]">
        {/* LEFT ── Incoming issuer authorization request */}
        <div className="p-5 border-r border-white/5" data-testid="demo-agent-panel">
          <div className="text-[9px] font-bold text-slate-500 tracking-widest mb-3">
            STEP 1 &middot; ISSUER SENDS AUTHORIZATION CALLBACK
          </div>
          <AnimatePresence mode="wait">
            {step >= 1 && (
              <motion.div
                key="request"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="border border-amber-500/25 rounded-lg p-4 bg-amber-500/[0.04] font-mono text-xs text-slate-200 leading-relaxed"
              >
                <div className="text-amber-400 mb-2">AuthorizationRequest {'{'}</div>
                <div className="pl-3">cardholder_id: <span className="text-cyan-300">"agent-hotel-042"</span>,</div>
                <div className="pl-3">merchant_id: <span className="text-cyan-300">"merchant_hotels"</span>,</div>
                <div className="pl-3 flex items-center gap-2">
                  amount_cents: <span className="text-red-400 font-bold">60000</span>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: step >= 2 ? 1 : 0 }}
                    className="text-[10px] text-red-400"
                  >
                    &larr; mandate says 20000
                  </motion.span>
                </div>
                <div className="pl-3">mcc_category: <span className="text-cyan-300">"travel_hotel"</span>,</div>
                <div className="pl-3">kypa_credential: <span className="text-cyan-300">"eyJhbGci..."</span>,</div>
                <div className="text-amber-400">{'}'}</div>
                <div className="mt-3 text-[10px] text-slate-500">
                  Network is waiting on approve / decline. Two-second window.
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* CENTER ── Callback code emitting live */}
        <div className="p-5 border-r border-white/5" data-testid="demo-callback-panel">
          <div className="text-[9px] font-bold text-slate-500 tracking-widest mb-3 flex items-center gap-2">
            STEP 2 &middot; PROGRAM OWNER CALLBACK RUNS
            {step === 3 && <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />}
          </div>
          <div className="border border-white/10 rounded-lg p-4 bg-slate-900 font-mono text-[11px] leading-relaxed min-h-[280px]">
            <div className="text-slate-500 mb-2 text-[10px]">on_authorization()</div>
            {CALLBACK_LINES.slice(0, typedLines).map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className={
                  line.startsWith('async def') || line.startsWith('from') || line.startsWith('kypa =')
                    ? 'text-emerald-300'
                    : line.trim().startsWith('#')
                      ? 'text-slate-500'
                      : line.includes('verdict.refused') || line.includes('return')
                        ? 'text-amber-300'
                        : 'text-slate-200'
                }
              >
                {line === '' ? '\u00a0' : line}
              </motion.div>
            ))}

            {/* Verify HTTP call state */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 text-cyan-300 text-[10px] flex items-center gap-2"
                data-testid="demo-verify-firing"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                POST kojiru.com/api/kypa/credential/verify
              </motion.div>
            )}

            {step >= 4 && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 border-l-2 border-red-500 pl-2 text-[10px] text-slate-300"
              >
                <div className="text-red-400 font-bold">\u2190 402 Payment Required</div>
                <div>&nbsp;&nbsp;refused: true</div>
                <div>&nbsp;&nbsp;reason: <span className="text-red-300">"mandate_scope_exceeded"</span></div>
                <div>&nbsp;&nbsp;detail: "60000 exceeds 20000"</div>
              </motion.div>
            )}
          </div>
        </div>

        {/* RIGHT ── Verdict panel */}
        <div className="p-5" data-testid="demo-verdict-panel">
          <div className="text-[9px] font-bold text-slate-500 tracking-widest mb-3">
            STEP 3 &middot; VERDICT INSIDE THE 2s WINDOW
          </div>
          <AnimatePresence>
            {step < 5 && (
              <motion.div
                key="pending"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                exit={{ opacity: 0 }}
                className="border border-white/10 rounded-lg p-6 bg-slate-900/40 text-center min-h-[280px] flex flex-col items-center justify-center"
              >
                <Loader2 className="w-6 h-6 text-slate-500 animate-spin mb-3" />
                <div className="text-xs text-slate-500 font-mono">awaiting verdict...</div>
              </motion.div>
            )}
            {step >= 5 && (
              <motion.div
                key="verdict"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="border-2 border-red-500 rounded-lg p-5 bg-red-500/[0.06] min-h-[280px] flex flex-col"
                data-testid="demo-final-verdict"
              >
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="text-red-400 font-bold text-sm tracking-wide">DECLINE</span>
                </div>
                <div className="text-xs text-slate-300 leading-relaxed mb-3">
                  Callback returned:
                </div>
                <pre className="bg-slate-950 rounded-md p-3 text-[11px] font-mono text-amber-300 border border-white/5">
{`("decline",
 "mandate_scope_exceeded")`}
                </pre>
                <div className="text-[10px] text-slate-400 leading-relaxed mt-4 space-y-1.5">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Network never authorizes the transaction</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>No chargeback risk. Consumer whole by construction.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Callback returned in under 500&thinsp;ms &mdash; well inside the 2s window.</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer strip */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-t border-white/5">
        <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500">
          <PlayCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span>Live composition &middot; <code className="text-cyan-300">examples/issuer_callback_refusal.py</code></span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/downloads/for-issuers-callback-demo.cast?v=13"
            className="text-[10px] font-bold text-cyan-300 hover:text-cyan-200 tracking-wider"
            data-testid="cast-download-link"
          >
            asciinema .cast &darr;
          </a>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <Repeat className="w-3 h-3" />
            <span>replays every ~20s</span>
          </div>
        </div>
      </div>
    </div>
  );
}
